import React, { useContext, useState, useEffect, ReactNode } from 'react';
import { createURL } from '@sm/utils';
import { defineMessages, t } from '@sm/intl';
import { generateMetricsAttribute } from '@sm/metrics';
import { Button } from '@wds/button';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Grid } from '@wds/grid';
import { Input } from '@wds/input';
import { Checkbox } from '@wds/checkbox';
import { StaticContext } from '@sm/webassets/StaticContext';
import { Recaptcha, useRecaptcha } from '@sm/webassets/Recaptcha';
import classnames from 'classnames';

import { Button as CoreButton } from '../core/Button';
import useStyles from './useStyles';
import { CONTENTEXP_755 } from '~app/pages/experiments';

const COPY = defineMessages({
  HERO_FORM_SOCIAL_CTA_COPY: {
    id: 'HeroFormCTA.SocialSignUp',
    defaultMessage: 'Or sign in with',
    description: '[Type: Label][Vis: med] - Informs the viewer they can sign in with social media',
  },
});

export type Props = {
  className: string;
  ctaFormSubmit: string;
  ctaFormEmail: string;
  ctaFormEmailPlaceholder: string;
  ctaFormNewsletterOptOut: string;
  ctaFormPassword: string;
  ctaFormPasswordPlaceholder: string;
  ctaFormPrivacyConsent: ReactNode;
  ctaFacebook?: string;
  ctaGoogle?: string;
  /** Disclaimer HTML string */
  disclaimer: string;
  utSource?: string;
  utSource2?: string;
  boldHeader?: string;
  subHeader?: string;
  showThirdPartySignUpActions?: boolean;
  showDisclaimer?: boolean;
  isEnterpriseThankYou?: boolean;
};

/** Type-wrapper for `Typography` to allow use of `dangerouslySetInnerHTML`  */
const TypographyWithInnerHTML = Typography as React.ForwardRefExoticComponent<
  Omit<React.ComponentProps<typeof Typography>, 'children'> & {
    dangerouslySetInnerHTML: { __html: string };
    children?: never;
  }
>;

const HeroFormCTA = ({
  className,
  ctaFormEmail,
  ctaFormEmailPlaceholder,
  ctaFormNewsletterOptOut,
  ctaFormPassword,
  ctaFormPasswordPlaceholder,
  ctaFormPrivacyConsent,
  ctaFormSubmit,
  ctaFacebook = 'Facebook',
  ctaGoogle = 'Google',
  disclaimer,
  utSource = '',
  utSource2 = '',
  boldHeader,
  subHeader,
  showThirdPartySignUpActions = true,
  showDisclaimer = true,
  isEnterpriseThankYou,
}: Props): React.ReactElement => {
  const utSource3 = 'HeroFormCTA';
  const actionType = 'click-url';
  const actionFlow = 'survey-endpage';
  const thirdPartySignUpUrl = '/user/third-party/';
  const isExperiment = utSource2 === 'RebrandEpFormV1';
  const {
    environment: { countryCode },
    smParams: { surveyId, collectorId, respondentId },
    templateType,
    contentexp755Treatment,
  } = useContext(StaticContext);

  const {
    hardRule,
    enterpriseThankYouForm,
    ctaFormField,
    ctaLabel,
    visuallyHidden,
    facebookButton,
    googleButton,
    experimentSpan,
    disclaimerContainer,
  } = useStyles({
    isEnterpriseThankYou,
    isExperiment,
  });

  const containerClasses = classnames('sm-hero-form-cta', className);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleEmailChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setEmail(e.target.value);
  };
  const handlePasswordChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setPassword(e.target.value);
  };

  const isTreatmentContentexp755 = contentexp755Treatment === CONTENTEXP_755.treatment;
  const treatmentURL = '/user/sign-up/';
  const controlURL = '/user/sign-up/';

  const recaptchaContext = useRecaptcha({
    actionName: 'user_signup',
    successCallback: undefined, // should not use for DOM manipulations
    siteKey: undefined, // set via StaticContext
    useEnterprise: undefined, // set via StaticContext
    errorCallback: undefined,
  });

  const amplitudeEventName = 'endpage user interaction';
  // submits the form action when reCAPTCHA sets the token properly
  useEffect(() => {
    if (recaptchaContext.greToken.length) {
      (document.getElementById('signup_form') as HTMLFormElement).submit();
    }
  }, [recaptchaContext.greToken]);

  return (
    <Box className={containerClasses} pt={7} data-testid="HeroFormCTA">
      {isExperiment ? (
        <Box>
          <Typography component="section" variant="sectionTitleSm" weight="medium" align="center">
            <span className={experimentSpan}>{boldHeader}</span>
          </Typography>
          <Box pt={3} pb={5}>
            <Typography component="div" variant="bodySm" align="center">
              {subHeader}
            </Typography>
          </Box>
        </Box>
      ) : (
        <></>
      )}
      <form
        id="signup_form"
        method="post"
        action={createURL(isTreatmentContentexp755 ? treatmentURL : controlURL, {
          ut_source: utSource,
          ut_source2: utSource2,
          ut_source3: utSource3,
          ut_ctatext: ctaFormSubmit,
        })}
        className={isEnterpriseThankYou ? enterpriseThankYouForm : undefined}
        data-testid="HeroFormCTA__Form"
        data-sm-metrics={generateMetricsAttribute({
          data: {
            actionType,
            actionFlow,
            log: {
              message: `${utSource}-${utSource2}-${utSource3}-hero-form-sign-up`,
            },
          },
        })}
      >
        <Recaptcha
          greToken={recaptchaContext.greToken}
          showError={recaptchaContext.showError}
          setShowError={recaptchaContext.setShowError}
        />
        <input type="hidden" name="username" className="input-username" value={email} />
        <input type="hidden" name="password_confirm" className="input-password_confirm" value={password} />
        <Box element="div" className={ctaFormField}>
          <label className={isEnterpriseThankYou ? visuallyHidden : ctaLabel} htmlFor="email-input">
            <Typography component="span" variant="bodySm">
              {ctaFormEmail}
            </Typography>
          </label>
          <Input
            id="email-input"
            type="email"
            name="email"
            placeholder={ctaFormEmailPlaceholder}
            required
            stretched
            onChange={handleEmailChange}
            value={email}
          />
        </Box>
        <Box element="div" className={ctaFormField}>
          <label className={isEnterpriseThankYou ? visuallyHidden : ctaLabel} htmlFor="password-input">
            <Typography component="span" variant="bodySm">
              {ctaFormPassword}
            </Typography>
          </label>
          {isTreatmentContentexp755 ? (
            <Input
              id="password-input"
              type="password"
              name="password"
              ariaLabel="treatment password"
              stretched
              onChange={handlePasswordChange}
              placeholder={ctaFormPasswordPlaceholder}
              value={password}
              required
            />
          ) : (
            <Input
              id="password-input"
              type="password"
              name="password"
              ariaLabel="control password"
              stretched
              onChange={handlePasswordChange}
              placeholder={ctaFormPasswordPlaceholder}
              value={password}
              required
            />
          )}
        </Box>
        {countryCode.toLowerCase() === 'de' && (
          <>
            <Box element="div" className={ctaFormField}>
              <Checkbox
                // weird required type from Wrench due to merge of HTMLAttribute and custom type
                // ((boolean | ReactChild | ReactFragment | ReactPortal | null) & string) | undefined
                label={ctaFormPrivacyConsent as unknown as ReactNode & string}
                id="privacy-consents"
                name="privacy-consents"
                required
              />
            </Box>
            <Box element="div" className={ctaFormField}>
              <Checkbox label={ctaFormNewsletterOptOut} id="subscribe-to-newsletter" name="subscribe-to-newsletter" />
            </Box>
          </>
        )}
        <Button
          size="lg"
          stretched
          onClick={recaptchaContext.fireGRE}
          data-sm-metrics={generateMetricsAttribute({
            data: {
              amplitudeEvent: amplitudeEventName,
              itemSelected: ctaFormSubmit,
              templateType,
              surveyId,
              collectorId,
              respondentId,
            },
          })}
        >
          {ctaFormSubmit}
        </Button>
      </form>
      {isExperiment ? (
        <Box mb={5} mt={5}>
          <Grid noBleeds>
            <Grid.Item>
              <Box mb={3} mt={3} className={hardRule}>
                <></>
              </Box>
            </Grid.Item>
            <Grid.Item>
              <Typography align="center" component="div" variant="bodySm" color="darkMuted">
                {t(COPY.HERO_FORM_SOCIAL_CTA_COPY)}
              </Typography>
            </Grid.Item>
            <Grid.Item>
              <Box mb={3} mt={3} className={hardRule}>
                <></>
              </Box>
            </Grid.Item>
          </Grid>
        </Box>
      ) : (
        <Box mb={4} mt={4} className={hardRule}>
          <></>
        </Box>
      )}
      {showThirdPartySignUpActions && (
        <Grid noBleeds>
          <Grid.Item xs={6}>
            {isTreatmentContentexp755 ? (
              <CoreButton
                className={facebookButton}
                stretched
                href={createURL(thirdPartySignUpUrl, {
                  third_party: 'facebook',
                  action: 'sign_up',
                })}
                data-sm-metrics={generateMetricsAttribute({
                  data: {
                    amplitudeEvent: amplitudeEventName,
                    itemSelected: ctaFacebook,
                    templateType,
                    surveyId,
                    collectorId,
                    respondentId,
                    actionType,
                    actionFlow,
                    log: {
                      message: `${utSource}-${utSource2}-${utSource3}-${ctaFacebook}-treatment`,
                    },
                  },
                })}
              >
                <span>{ctaFacebook}</span>
              </CoreButton>
            ) : (
              <CoreButton
                className={facebookButton}
                stretched
                href={createURL(thirdPartySignUpUrl, {
                  third_party: 'facebook',
                  action: 'sign_up',
                })}
                data-sm-metrics={generateMetricsAttribute({
                  data: {
                    amplitudeEvent: amplitudeEventName,
                    itemSelected: ctaFacebook,
                    templateType,
                    surveyId,
                    collectorId,
                    respondentId,
                    actionType,
                    actionFlow,
                    log: {
                      message: `${utSource}-${utSource2}-${utSource3}-${ctaFacebook}-control`,
                    },
                  },
                })}
              >
                <span>{ctaFacebook}</span>
              </CoreButton>
            )}
          </Grid.Item>
          <Grid.Item xs={6}>
            {isTreatmentContentexp755 ? (
              <CoreButton
                className={googleButton}
                stretched
                href={createURL(thirdPartySignUpUrl, {
                  third_party: 'google',
                  action: 'sign_up',
                })}
                data-sm-metrics={generateMetricsAttribute({
                  data: {
                    amplitudeEvent: amplitudeEventName,
                    itemSelected: ctaGoogle,
                    templateType,
                    surveyId,
                    collectorId,
                    respondentId,
                    actionType,
                    actionFlow,
                    log: {
                      message: `${utSource}-${utSource2}-${utSource3}-${ctaGoogle}-treatment`,
                    },
                  },
                })}
              >
                <span>{ctaGoogle}</span>
              </CoreButton>
            ) : (
              <CoreButton
                className={googleButton}
                stretched
                href={createURL(thirdPartySignUpUrl, {
                  third_party: 'google',
                  action: 'sign_up',
                })}
                data-sm-metrics={generateMetricsAttribute({
                  data: {
                    amplitudeEvent: amplitudeEventName,
                    itemSelected: ctaGoogle,
                    templateType,
                    surveyId,
                    collectorId,
                    respondentId,
                    actionType,
                    actionFlow,
                    log: {
                      message: `${utSource}-${utSource2}-${utSource3}-${ctaGoogle}-control`,
                    },
                  },
                })}
              >
                <span>{ctaGoogle}</span>
              </CoreButton>
            )}
          </Grid.Item>
        </Grid>
      )}
      {showDisclaimer && (
        <Box mt={5} className={disclaimerContainer}>
          <TypographyWithInnerHTML
            align="center"
            component="div"
            variant="bodySm"
            color="darkMuted"
            dangerouslySetInnerHTML={{ __html: `${disclaimer}` }} // eslint-disable-line jam3/no-sanitizer-with-danger
          />
        </Box>
      )}
    </Box>
  );
};

export default HeroFormCTA;
